import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ToolNameView } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost } from '../../../lib/api-client';

// F19.14: the tool names box 1 of the Ideal candidate profile offers, and ADMIN-19's list of them. The server pushes an
// invalidation of `['tool-names']` whenever the list changes (a request taught it a name, or HR deleted or merged one).

const TOOL_NAMES_KEY = ['tool-names'] as const;
const PICKABLE_KEY = [...TOOL_NAMES_KEY, 'pickable'] as const;
const ADMIN_KEY = [...TOOL_NAMES_KEY, 'admin'] as const;
/** The list changes when someone saves a request with a new tool; a minute old is fresh enough between pushes. */
const PICKABLE_STALE_MS = 60_000;
const NO_NAMES: readonly string[] = [];

/** The names box 1 suggests. Empty until they arrive, so the field never waits on them. */
export function useToolNames(): readonly string[] {
  const query = useQuery<readonly string[], ApiError>({
    queryKey: PICKABLE_KEY,
    queryFn: () => apiGet<readonly string[]>('/tool-names'),
    staleTime: PICKABLE_STALE_MS,
    placeholderData: (previous) => previous,
  });
  return query.data ?? NO_NAMES;
}

export function useAdminToolNames(): UseQueryResult<readonly ToolNameView[], ApiError> {
  return useQuery<readonly ToolNameView[], ApiError>({
    queryKey: ADMIN_KEY,
    queryFn: () => apiGet<readonly ToolNameView[]>('/admin/tool-names'),
    placeholderData: (previous) => previous,
  });
}

export type ToolNameChange =
  | { readonly kind: 'hide'; readonly entry: ToolNameView }
  | { readonly kind: 'restore'; readonly entry: ToolNameView }
  | { readonly kind: 'merge'; readonly entry: ToolNameView; readonly into: ToolNameView };

function optimistic(rows: readonly ToolNameView[], change: ToolNameChange): readonly ToolNameView[] {
  return rows.map((row) => {
    if (row.id !== change.entry.id) return row;
    if (change.kind === 'hide') return { ...row, isHidden: true };
    if (change.kind === 'restore') return { ...row, isHidden: false, mergedIntoName: null };
    return { ...row, isHidden: true, mergedIntoName: change.into.name };
  });
}

/** Applied on screen at once, rolled back if the server refuses (CLAUDE.md §5a rule 1). */
export function useChangeToolName() {
  const queryClient = useQueryClient();
  return useMutation<{ readonly toolName: ToolNameView }, ApiError, ToolNameChange, { readonly previous: readonly ToolNameView[] | undefined }>({
    mutationFn: (change) => {
      const path = `/admin/tool-names/${change.entry.id}`;
      if (change.kind === 'merge') return apiPost(`${path}/merge`, { version: change.entry.version, intoId: change.into.id });
      return apiPost(`${path}/${change.kind}`, { version: change.entry.version });
    },
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_KEY });
      const previous = queryClient.getQueryData<readonly ToolNameView[]>(ADMIN_KEY);
      if (previous) queryClient.setQueryData(ADMIN_KEY, optimistic(previous, change));
      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous) queryClient.setQueryData(ADMIN_KEY, context.previous);
    },
    // Awaited, so the optimistic row holds until the refetch lands (§5a rule 3).
    onSettled: () => queryClient.invalidateQueries({ queryKey: TOOL_NAMES_KEY }),
  });
}
